import {
    AlarmDefinitionChild,
    AlarmDefinitionUpdateRequest,
    BandDefinitionUpdateRequest,
    BaselineUpdateRequest,
    IAlarmDefinitionChild,
    IIndicatorResponse,
    IIndicatorUpdateRequest,
    IProfileChild,
    IProfileOptionsResponse,
    MetricClassificationSourceEnum,
    MetricTypeCodes,
} from '@kcftech/prometheus-client';
import { RadioButtonItem } from 'components/forms/RadioButtonGroup';
import { sdClient } from 'contexts/SDKClientContext';
import { OffThresholdClassification } from 'pages/workbench/types';
import { IndicatorThresholdColors } from 'utils/colors';
import { RequiredButAllowUndefined } from 'utils/types';

export const bandIndicatorTypes = new Set([
    MetricTypeCodes.BandPressure,
    MetricTypeCodes.VibrationBandMax,
    MetricTypeCodes.VibrationBandRms,
    MetricTypeCodes.VibrationBandMaxAccel,
    MetricTypeCodes.VibrationBandRmsAccel,
]);
export const bandAccelIndicatorTypes = new Set([
    MetricTypeCodes.VibrationBandMaxAccel,
    MetricTypeCodes.VibrationBandRmsAccel,
]);
export const bandVelIndicatorTypes = new Set([
    MetricTypeCodes.BandPressure,
    MetricTypeCodes.VibrationBandMax,
    MetricTypeCodes.VibrationBandRms,
]);

export const hasBandDefinition = (indicatorType: MetricTypeCodes) =>
    bandIndicatorTypes.has(indicatorType);

export const isDamageIndicator = (indicatorType: MetricTypeCodes) =>
    indicatorType === MetricTypeCodes.DamageAccumulationPressure ||
    indicatorType === MetricTypeCodes.DamageAccumulationAccel;

export const offThresholdClassifications: RadioButtonItem<OffThresholdClassification>[] =
    [
        { value: 'Set', label: 'Set Value', key: 'Set' },
        { value: 'Inherit', label: 'Inherit', key: 'Inherit' },
        { value: 'Unset', label: 'Unset', key: 'Unset' },
    ];

export const convertOffThresholdClassificationToEnum = (
    classification: OffThresholdClassification,
): MetricClassificationSourceEnum => {
    switch (classification) {
        case 'Set':
            return MetricClassificationSourceEnum.Self;
        case 'Inherit':
            return MetricClassificationSourceEnum.Inherit;
        case 'Unset':
            return MetricClassificationSourceEnum.None;
        default:
            return MetricClassificationSourceEnum.None;
    }
};

export const genericIndicatorTypes = new Set<string>([
    MetricTypeCodes.GeneralTimeSeries,
    MetricTypeCodes.GeneralizedAtoD,
]);

/**
 * Determines whether the indicator is a generic type
 * (used to determine whether the indicator requires custom units, for example)
 * @param indicatorType The type of indicator
 * @returns Whether the indicator of the given type is generic
 */
export const isGeneric = (indicatorType: string) =>
    genericIndicatorTypes.has(indicatorType);

export const runningSpeedIndicatorTypes = new Set<string>([
    MetricTypeCodes.RunningSpeed,
    MetricTypeCodes.AutoRunningSpeed,
]);

/**
 * Returns whether or not an indicatorType is a "Running Speed" indicator.
 */
export const isRunningSpeed = (indicatorType: string) =>
    runningSpeedIndicatorTypes.has(indicatorType);

/**
 * Gets the description for a given indicator type
 * @param indicatorType The indicator type
 * @returns The description
 */
export const getDescriptionForIndicatorType = (indicatorType: string) => {
    switch (indicatorType) {
        case MetricTypeCodes.VibrationBandMax:
        case MetricTypeCodes.VibrationBandMaxAccel:
            return 'Trend the amplitude of specific vibration frequencies';
        case MetricTypeCodes.VibrationBandRms:
            return 'Trend the effective average of specific vibration frequencies';
        case MetricTypeCodes.BandPressure:
            return 'Trend the amplitude of specific pressure frequencies';
        case MetricTypeCodes.RmsPressure:
            return 'Trend the effective average pressure in each measurement set';
        case MetricTypeCodes.NegativePeakPressure:
            return 'Trend the lowest pressure level in each measurement set';
        case MetricTypeCodes.PositivePeakPressure:
            return 'Trend the highest pressure level in each measurement set';
        case MetricTypeCodes.Pressure:
            return 'Trend the average pressure of each measurement set';
        case MetricTypeCodes.OilHumidity:
            return 'Trend the oil humidity from an oil sensor';
        case MetricTypeCodes.OilTemperature:
            return 'Trend the oil temperature from an oil sensor';
        case MetricTypeCodes.GeneralTimeSeries:
            return 'Trend data from a customer specific sensor';
        case MetricTypeCodes.GeneralizedAtoD:
            return 'Trend additional health metrics such as pressure and oil moisture content';
        case MetricTypeCodes.SignalStrength:
            return 'Trend the wireless efficiency of a sensor';
        case MetricTypeCodes.Temperature:
            return 'Trend the ambient temperature of a sensor';
        case MetricTypeCodes.Voltage:
            return "Trend the voltage of your sensor's batteries";
        case MetricTypeCodes.DamageAccumulationPressure:
        case MetricTypeCodes.DamageAccumulationAccel:
            return 'Trend the current rate of damage to your machine against a baseline of your choosing';
        case MetricTypeCodes.VibrationOverall:
        case MetricTypeCodes.VibrationOverallAccel:
            return 'Trend the total sum of the vibration measured in the frequency spectrum';
        case MetricTypeCodes.VibrationOverallRms:
        case MetricTypeCodes.VibrationOverallRmsAccel:
            return 'Trend the effective average vibration level in each measurement set';
        case MetricTypeCodes.VibrationOverallPeak:
        case MetricTypeCodes.VibrationOverallPeakAccel:
            return 'Trend the highest vibration level in each measurement set';
        case MetricTypeCodes.VibrationOverallKurtosis:
        case MetricTypeCodes.VibrationOverallKurtosisAccel:
            return 'Trend the outlier behavior of each measurement set by comparing peaks greater than 3 standard deviations and the RMS value';
        case MetricTypeCodes.VibrationOverallCrestFactorAccel:
            return 'Trend the impact behavior of each measurement set by comparing the peak value to the RMS value';
        case MetricTypeCodes.VibrationOverallSkewness:
        case MetricTypeCodes.VibrationOverallSkewnessAccel:
            return 'Trend the symmetry of each measurement set by comparing each value to the mean';
        case MetricTypeCodes.AnalogElectricalOverallCurrent:
        case MetricTypeCodes.AnalogElectricalOverallVoltage:
            return 'Trend the average signal level in each measurement set';
        case MetricTypeCodes.AnalogElectricalAcMinimumCurrent:
        case MetricTypeCodes.AnalogElectricalAcMinimumVoltage:
            return 'Trend the highest value in each measurement set, after removing the D/C component from the waveform';
        case MetricTypeCodes.AnalogElectricalAcMaximumCurrent:
        case MetricTypeCodes.AnalogElectricalAcMaximumVoltage:
            return 'Trend the lowest value in each measurement set, after removing the D/C component from the waveform';
        case MetricTypeCodes.AnalogElectricalAcRmsCurrent:
        case MetricTypeCodes.AnalogElectricalAcRmsVoltage:
            return 'Trend the effective average of the measurement set, after removing the D/C component from the waveform';
        case MetricTypeCodes.AnalogElectricalDcMinimumCurrent:
        case MetricTypeCodes.AnalogElectricalDcMinimumVoltage:
            return 'Trend the highest value in each measurement set';
        case MetricTypeCodes.AnalogElectricalDcMaximumCurrent:
        case MetricTypeCodes.AnalogElectricalDcMaximumVoltage:
            return 'Trend the lowest value in each measurement set';
        case MetricTypeCodes.AnalogElectricalDcRmsCurrent:
        case MetricTypeCodes.AnalogElectricalDcRmsVoltage:
            return 'Trend the effective average of the measurement set';
        default:
            return '';
    }
};

/**
 * Gets the units for a given indicator type
 * @param indicatorType The indicator type
 * @returns The units
 */
export const getIndicatorTypeUnits = (
    indicatorType: string,
    profileOptions: IProfileOptionsResponse | undefined,
    profile: IProfileChild | undefined,
) => {
    switch (indicatorType) {
        case MetricTypeCodes.VibrationBandMax:
        case MetricTypeCodes.VibrationOverall:
        case MetricTypeCodes.VibrationOverallPeak:
        case MetricTypeCodes.VibrationBandRms:
        case MetricTypeCodes.VibrationOverallRms:
            return (
                profileOptions?.VelocityUnits.find(
                    (option) => option.Value === profile?.VelocityUnits,
                )?.Text ?? profileOptions?.VelocityUnits[0].Text
            );
        case MetricTypeCodes.VibrationBandMaxAccel:
        case MetricTypeCodes.VibrationOverallAccel:
        case MetricTypeCodes.VibrationOverallKurtosisAccel:
        case MetricTypeCodes.VibrationOverallPeakAccel:
        case MetricTypeCodes.VibrationOverallRmsAccel:
            return (
                profileOptions?.AccelerationUnits.find(
                    (option) => option.Value === profile?.AccelerationUnits,
                )?.Text ?? profileOptions?.AccelerationUnits[0].Text
            );
        case MetricTypeCodes.BandPressure:
        case MetricTypeCodes.RmsPressure:
        case MetricTypeCodes.NegativePeakPressure:
        case MetricTypeCodes.PositivePeakPressure:
        case MetricTypeCodes.Pressure:
            return profile?.PressureUnits ?? 'pascal';
        case MetricTypeCodes.SignalStrength:
        case MetricTypeCodes.OilHumidity:
            return '%';
        case MetricTypeCodes.GeneralTimeSeries:
            return 'Series';
        case MetricTypeCodes.GeneralizedAtoD:
            return 'General Value';
        case MetricTypeCodes.OilTemperature:
        case MetricTypeCodes.Temperature:
            return profile?.TemperatureUnits ?? 'Fahrenheit';
        case MetricTypeCodes.Voltage:
            return 'V';
        case MetricTypeCodes.DamageAccumulationAccel:
        case MetricTypeCodes.DamageAccumulationPressure:
            return 'Damage';
        case MetricTypeCodes.VibrationOverallKurtosis:
            return 'Kurtosis';
        case MetricTypeCodes.VibrationOverallCrestFactorAccel:
            return 'Crest Factor';
        case MetricTypeCodes.VibrationOverallSkewness:
        case MetricTypeCodes.VibrationOverallSkewnessAccel:
            return 'Skewness';
        default:
            return 'Value';
    }
};

export const getIndicatorDisplayUnits = (
    indicator: Pick<IIndicatorResponse, 'IndicatorType' | 'DisplayUnits'>,
    profileOptions: IProfileOptionsResponse | undefined,
    profile: IProfileChild | undefined,
) => {
    const units = getIndicatorTypeUnits(indicator.IndicatorType, profileOptions, profile);
    return units === undefined || units === 'Series' ? indicator.DisplayUnits : units;
};

/**
 * Threshold definitions for the Trend.
 */
export const thresholdDefinitions = [
    {
        color: IndicatorThresholdColors.alarm,
        className: 'hi-alarm' as const,
        dir: 'up',
        value: (def: AlarmDefinitionChild) => def.HighAlarmValue,
        enabled: (def: AlarmDefinitionChild) => def.HighAlarmValue !== undefined,
    },
    {
        color: IndicatorThresholdColors.alarm,
        className: 'lo-alarm' as const,
        dir: 'down',
        value: (def: AlarmDefinitionChild) => def.LowAlarmValue,
        enabled: (def: AlarmDefinitionChild) => def.LowAlarmValue !== undefined,
    },
    {
        color: IndicatorThresholdColors.warning,
        className: 'hi-warn' as const,
        dir: 'up',
        value: (def: AlarmDefinitionChild) => def.HighWarningValue,
        enabled: (def: AlarmDefinitionChild) => def.HighWarningValue !== undefined,
    },
    {
        color: IndicatorThresholdColors.warning,
        className: 'lo-warn' as const,
        dir: 'down',
        value: (def: AlarmDefinitionChild) => def.LowWarningValue,
        enabled: (def: AlarmDefinitionChild) => def.LowWarningValue !== undefined,
    },
    {
        color: IndicatorThresholdColors.off,
        className: 'off' as const,
        dir: 'down',
        value: (def: AlarmDefinitionChild) => def.OffThresholdValue,
        enabled: (def: AlarmDefinitionChild) => def.OffThresholdValue !== undefined,
    },
];

export const getNormalThresholdValue = (def: AlarmDefinitionChild) => {
    if (def.HighAlarmValue !== undefined || def.HighWarningValue !== undefined) {
        return def.HighWarningValue !== undefined
            ? def.HighWarningValue
            : (def.HighAlarmValue as number);
    }
    if (def.LowAlarmValue !== undefined || def.LowWarningValue !== undefined) {
        return def.LowWarningValue !== undefined
            ? def.LowWarningValue
            : (def.LowAlarmValue as number);
    }
    if (def.OffThresholdValue !== undefined) {
        return def.OffThresholdValue;
    }
    return -1000;
};

/**
 * Returns whether or not an indicator has thresholds set.
 */
export const hasThresholds = ({
    AlarmDefinition: alarmDefinition,
}: IIndicatorResponse) => {
    if (!alarmDefinition) {
        return false;
    }
    return thresholdDefinitions.some((threshold) => threshold.enabled(alarmDefinition));
};

/**
 * Builds an indicator update request using the existing indicator information.
 * @param indicatorId The indicator id to be updated
 */
export async function buildInitialIndicatorRequest(
    indicatorId: string,
): Promise<RequiredButAllowUndefined<IIndicatorUpdateRequest>> {
    const {
        AlarmDefinition,
        MetaData,
        MeasureOf,
        Baseline,
        BandDefinition,
        Name,
        CustomUnits,
        IsHardwareFiltered,
        CustomRoleType,
    } = await sdClient.getIndicator({ indicatorId });

    return {
        Name,
        AlarmDefinition: new AlarmDefinitionUpdateRequest({
            ...AlarmDefinition,
            Classification: AlarmDefinition
                ? AlarmDefinition.Classification
                : MetricClassificationSourceEnum.None,
        }),
        MetaData,
        DamageExponent: undefined,
        MeasureOf,
        Baseline: new BaselineUpdateRequest({
            StartDate: Baseline?.BaselineRangeStartDate,
            EndDate: Baseline?.BaselineRangeEndDate,
        }),
        BandDefinition:
            BandDefinition === undefined
                ? undefined
                : new BandDefinitionUpdateRequest({
                      ...BandDefinition,
                  }),
        CustomUnits,
        IsHardwareFiltered,
        CustomRoleType,
    };
}

export const isIndicatorEditable = (indicatorType: string | undefined) =>
    indicatorType !== undefined &&
    indicatorType !== 'Invalid' &&
    !isRunningSpeed(indicatorType);

type AlarmLevels = Pick<
    IAlarmDefinitionChild,
    | 'CriticalLowAlarmValue'
    | 'LowAlarmValue'
    | 'LowWarningValue'
    | 'HighAlarmValue'
    | 'CriticalHighAlarmValue'
    | 'HighWarningValue'
    | 'OffThresholdValue'
>;

export const validateAlarmLevels = ({
    CriticalHighAlarmValue,
    HighAlarmValue,
    HighWarningValue,
    LowWarningValue,
    LowAlarmValue,
    CriticalLowAlarmValue,
    OffThresholdValue,
}: AlarmLevels): string | undefined => {
    if (CriticalHighAlarmValue !== undefined) {
        if (HighAlarmValue !== undefined && CriticalHighAlarmValue <= HighAlarmValue) {
            return 'Critical High Alarm must be greater than High Alarm';
        }

        if (
            HighWarningValue !== undefined &&
            CriticalHighAlarmValue <= HighWarningValue
        ) {
            return 'Critical High Alarm must be greater than High Warning';
        }

        if (LowWarningValue !== undefined && CriticalHighAlarmValue <= LowWarningValue) {
            return 'Critical High Alarm must be greater than Low Warning';
        }

        if (LowAlarmValue !== undefined && CriticalHighAlarmValue <= LowAlarmValue) {
            return 'Critical High Alarm must be greater than Low Alarm';
        }

        if (
            CriticalLowAlarmValue !== undefined &&
            CriticalHighAlarmValue <= CriticalLowAlarmValue
        ) {
            return 'Critical High Alarm must be greater than Critical Low Alarm';
        }

        if (
            OffThresholdValue !== undefined &&
            CriticalHighAlarmValue <= OffThresholdValue
        ) {
            return 'Critical High Alarm must be greater than Off Threshold';
        }
    }

    if (HighAlarmValue !== undefined) {
        if (HighWarningValue !== undefined && HighAlarmValue <= HighWarningValue) {
            return 'High Alarm must be greater than High Warning';
        }

        if (LowWarningValue !== undefined && HighAlarmValue <= LowWarningValue) {
            return 'High Alarm must be greater than Low Warning';
        }

        if (LowAlarmValue !== undefined && HighAlarmValue <= LowAlarmValue) {
            return 'High Alarm must be greater than Low Alarm';
        }

        if (
            CriticalLowAlarmValue !== undefined &&
            HighAlarmValue <= CriticalLowAlarmValue
        ) {
            return 'High Alarm must be greater than Critical Low Alarm';
        }

        if (OffThresholdValue !== undefined && HighAlarmValue <= OffThresholdValue) {
            return 'High Alarm must be greater than Off Threshold';
        }
    }

    if (HighWarningValue !== undefined) {
        if (LowWarningValue !== undefined && HighWarningValue <= LowWarningValue) {
            return 'High Warning must be greater than Low Warning';
        }

        if (LowAlarmValue !== undefined && HighWarningValue <= LowAlarmValue) {
            return 'High Warning must be greater than Low Alarm';
        }

        if (
            CriticalLowAlarmValue !== undefined &&
            HighWarningValue <= CriticalLowAlarmValue
        ) {
            return 'High Warning must be greater than Critical Low Alarm';
        }

        if (OffThresholdValue !== undefined && HighWarningValue <= OffThresholdValue) {
            return 'High Warning must be greater than Off Threshold';
        }
    }

    if (LowWarningValue !== undefined) {
        if (LowAlarmValue !== undefined && LowWarningValue <= LowAlarmValue) {
            return 'Low Warning must be greater than Low Alarm';
        }

        if (
            CriticalLowAlarmValue !== undefined &&
            LowWarningValue <= CriticalLowAlarmValue
        ) {
            return 'Low Warning must be greater than Critical Low Alarm';
        }

        if (OffThresholdValue !== undefined && LowWarningValue <= OffThresholdValue) {
            return 'Low Warning must be greater than Off Threshold';
        }
    }

    if (LowAlarmValue !== undefined) {
        if (
            CriticalLowAlarmValue !== undefined &&
            LowAlarmValue <= CriticalLowAlarmValue
        ) {
            return 'Low Alarm must be greater than Critical Low Alarm';
        }

        if (OffThresholdValue !== undefined && LowAlarmValue <= OffThresholdValue) {
            return 'Low Alarm must be greater than Off Threshold';
        }
    }

    if (
        CriticalLowAlarmValue !== undefined &&
        OffThresholdValue !== undefined &&
        CriticalLowAlarmValue <= OffThresholdValue
    ) {
        return 'Critical Low Alarm must be greater than Off Threshold';
    }

    return undefined;
};

/**
 * takes in the classification from the api and returns the typed OffThresholdClassification in order to determine the initial radio button
 * @param classification the classification from the api
 * @returns an OffThresholdClassification
 */
export const convertAlarmDefinitionClassification = (
    classification: string | undefined,
): OffThresholdClassification => {
    switch (classification) {
        case 'Self':
            return 'Set';
        case 'Inherit':
            return 'Inherit';
        case 'None':
            return 'Unset';
        default:
            return 'Set';
    }
};
